import { useState, useEffect, useRef } from "react";
import { getDashboardData } from "../../../api/getDashboardData";
import OverallBarChart from "./OverallBarChart";
import FiltersMenu from "./FiltersMenu.jsx";
import { Grid, Paper, Skeleton, Button, Box } from "@mui/material";
import { LoadsRadarChart } from "./LoadsRadarChart.jsx";
import OverallComparisonBarChart from "./OverallComparisonBarChart.jsx";
import { PercentageHistogramChart } from "./PercentageHistogramChart.js.jsx";
import PercChangeHistogramChart from "./PercChangeHistogram.jsx";
import { useTranslation } from "react-i18next";
import { RoleComponentGuard } from "../../Reuseables/Guards/RoleComponentGuard";
import { useApplicationContext } from "../../ContextProviders/ApplicationContext.js";
import ActivityComparisonBarChart from "./ActivityComparisonBarChart.js";
import { ActivityData, ChartData } from "../../../types/chartData";
import { Role, Stage } from "../../../types/enums";
import { StageView } from "../../../types/stageView";
import { Project } from "../../../types/project";
import { useOrganizationContext } from "../../ContextProviders/OrganizationContext";
import GenerateDashboardPPTXPopup from "./Powerpoint/GenerateDashboardPPTXPopup";

// Variables for project comparison

type DashboardViewProps = {
  pc?: string;
  hideBackBtn?: boolean;
  setView?: (s: StageView) => void;
};

export default function DashboardView({
  hideBackBtn,
  pc,
  setView,
}: DashboardViewProps) {
  const { t } = useTranslation();
  const { activeWorkspace } = useOrganizationContext();
  const { showSnackbar } = useApplicationContext();
  const [selectedProject, setSelectedProject] = useState<Project | null>(null);
  const [data, setData] = useState<ChartData | null>(null);
  const [startDate, setStartDate] = useState<Date | null>(null);
  const [endDate, setEndDate] = useState<Date | null>(null);
  const [selectedActivity, setSelectedActivity] = useState<string>(
    t("overall")
  );
  const [activityData, setActivityData] = useState<ActivityData | null>(null);
  const [attribute, setAttribute] = useState("MLI");
  const [availableProjects, setAvailableProjects] = useState<Project[] | null>(
    null
  );
  //Comparison project.
  const [compProject, setCompProject] = useState<Project | null>(null);
  const [compData, setCompData] = useState<ChartData | null>(null);
  const [compStartdate, setCompStartdate] = useState<Date | null>(null);
  const [compEnddate, setCompEnddate] = useState<Date | null>(null);
  const [compActivityData, setCompActivityData] = useState<ActivityData | null>(
    null
  );
  const [compSelectedActivity, setCompSelectedActivity] = useState<
    string | null
  >(null);

  const gridRef = useRef<HTMLDivElement>(null);

  const [radarGridHeight, setRadarGridHeight] = useState(0);

  const [powerpointPopup, setPowerpointPopup] = useState(false);

  useEffect(() => {
    if (activeWorkspace?.projects?.length === 0) {
      return;
    }

    const filteredProjects = activeWorkspace.projects?.filter(
      (p) => p.stage >= Stage.Started
    );

    let project: Project | undefined;

    if (!pc) {
      if (filteredProjects && filteredProjects.length > 0) {
        // NO PC Provided.
        setSelectedProject(null)
        return;
      }
    }

    const p = filteredProjects?.find((p) => p.projectCode === pc);
    if (p) {
      project = p;
    } else if (!project && filteredProjects && filteredProjects.length > 0) {
      project = filteredProjects[0];
    }

    if (!project) return;

    const start = new Date(project.startDate1 ?? new Date());
    const end = project.endDate1 ? new Date(project.endDate1) : new Date();

    setStartDate(start);
    setEndDate(end);
    setSelectedProject(project);

    FetchPrimaryProjectData(start, end, project.projectCode);

    // eslint-disable-next-line
  }, [activeWorkspace]);

  const FetchPrimaryProjectData = async (
    sDate: Date,
    eDate: Date,
    projectCode: string
  ) => {
    const formattedStartDate = sDate.toISOString().split("T")[0];
    const formattedEndDate = eDate.toISOString().split("T")[0];
    try {
      setIsLoading(true);
      const result = await getDashboardData(
        projectCode,
        formattedStartDate,
        formattedEndDate
      );
      setData(result);
    } catch (err: unknown) {
      if (err instanceof Error) {
        showSnackbar(err.message, "error");
      } else {
        showSnackbar("Unknown error", "error");
      }
    } finally {
      setIsLoading(false);
    }
  };
  const FetchComparisonProjectData = async (
    sDate: Date,
    eDate: Date,
    projectCode: string
  ) => {
    try {
      const formattedStartDate = sDate.toISOString().split("T")[0];
      const formattedEndDate = eDate.toISOString().split("T")[0];
      setIsLoadingComp(true);
      const result = await getDashboardData(
        projectCode,
        formattedStartDate,
        formattedEndDate
      );
      setCompData(result);
      setCompSelectedActivity(t("overall"));
    } catch (err: unknown) {
      if (err instanceof Error) {
        showSnackbar(err.message, "error");
      } else {
        showSnackbar("Unknown error", "error");
      }
    } finally {
      setIsLoadingComp(false);
    }
  };

  useEffect(() => {
    if (selectedProject) {
      if (compProject) {
        if (selectedProject.deviceType[0] !== compProject.deviceType[0]) {
          setCompProject(null);
          setCompData(null);
          setCompActivityData(null);
          setCompSelectedActivity(null);
          setCompStartdate(null);
          setCompEnddate(null);
        }
      }
    }
    // eslint-disable-next-line
  }, [selectedProject]);

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isLoadingComp, setIsLoadingComp] = useState(false);

  const getActivityData = (
    data: ChartData | null,
    isComparison: boolean,
    selectedActivity?: string | null
  ) => {
    if (!data) return null;
    if (selectedActivity === t("overall")) {
      let chartData = data as ChartData;
      return chartData.activities[0];
    }

    var activityDataTmp = data.activities.find(
      (activity) => activity.activityName === selectedActivity
    );

    if (activityDataTmp === undefined) {
      if (isComparison) setCompSelectedActivity(t("overall"));
      else {
        setSelectedActivity(t("overall"));
      }
      return data.activities[0];
    } else {
      return activityDataTmp;
    }
  };

  useEffect(() => {
    if (data) {
      setActivityData(getActivityData(data, false));
    }
    // eslint-disable-next-line
  }, [data]);

  useEffect(() => {
    if (compData) {
      setCompActivityData(getActivityData(compData, false));
    }
    // eslint-disable-next-line
  }, [compData]);

  useEffect(() => {
    setActivityData(getActivityData(data, false, selectedActivity));
    // eslint-disable-next-line
  }, [selectedActivity]);

  useEffect(() => {
    setCompActivityData(getActivityData(compData, true, compSelectedActivity));
    // eslint-disable-next-line
  }, [compSelectedActivity]);

  const updateGridHeight = () => {
    // Use the ref to get the current height of the grid
    if (gridRef.current && gridRef.current.clientHeight) {
      const height = gridRef.current.clientHeight;
      setRadarGridHeight(height);
    }
  };
  useEffect(() => {
    // Call the function initially when the component mounts
    updateGridHeight();

    // Add an event listener to update the height when the window is resized
    window.addEventListener("resize", updateGridHeight);

    // Clean up the event listener on component unmount
    return () => {
      window.removeEventListener("resize", updateGridHeight);
    };
  }, []);

  const heightGrid1 = "20vh";

  // function isActivityData(obj: any): obj is ActivityData {
  //   return obj && typeof obj.average === "number";
  // }

  // function isChartData(obj: any): obj is ChartData {
  //   return obj && isActivityData(obj.overall);
  // }

  return (
    <Grid
      paddingLeft={{ xs: 0, sm: 0, md: 0, lg: 0, xl: 30 }}
      paddingRight={{ xs: 0, sm: 0, md: 0, lg: 0, xl: 30 }}
      container
      sx={{
        flexDirection: {
          xs: "column",
          sm: "column",
          md: "column",
          lg: "column",
          xl: "column",
        },
      }}
      minHeight={"83vh"}
      spacing={2}
    >
      <Grid item>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            marginTop: 1,
            marginBottom: 1,
          }}
        >
          {hideBackBtn ? null : (
            <Button
              aria-label="Back"
              variant="contained"
              onClick={() => {
                if (selectedProject)
                  if (setView) setView({ stage: selectedProject.stage });
              }}
            >
              {t("buttons.back")}
            </Button>
          )}
          <RoleComponentGuard roles={[Role.PrecureAdmin]}>
            <Button
              disabled={data == null}
              variant="contained"
              onClick={() => {
                if (setView)
                  setView({
                    stage: 9,
                    props: { data, selectedProject, startDate, endDate },
                  });
              }}
            >
              Generate PDF
            </Button>
            <Button
              disabled={data == null}
              variant="contained"
              onClick={() => {
               setPowerpointPopup(true)
              }}
            >
              Create PPTX
            </Button>
          </RoleComponentGuard>
        </Box>

        <Grid
          container
          spacing={2}
          sx={{ flexDirection: { xs: "column", sm: "column", md: "row" } }}
        >
          <Grid item xs>
            <FiltersMenu
              startDate={startDate}
              endDate={endDate}
              setStartDate={setStartDate}
              setEndDate={setEndDate}
              selectedProject={selectedProject}
              setSelectedProject={setSelectedProject}
              projects={activeWorkspace?.projects?.filter(
                (p) => p.stage >= Stage.Started
              )}
              selectedActivity={selectedActivity}
              selectedActivityChanged={(event) => {
                setSelectedActivity(event.target.value);
              }}
              attribute={attribute}
              setAttribute={setAttribute}
              data={data}
              isLoading={isLoading}
              onProjectChanged={FetchPrimaryProjectData}
            />
          </Grid>
          <Grid item xs>
            <FiltersMenu
              startDate={compStartdate}
              endDate={compEnddate}
              setStartDate={setCompStartdate}
              setEndDate={setCompEnddate}
              selectedProject={compProject}
              setSelectedProject={setCompProject}
              projects={
                activeWorkspace.projects?.filter(
                  (p) =>
                    p.deviceType[0] === selectedProject?.deviceType[0] &&
                    p.stage >= Stage.Started
                ) ?? []
              }
              selectedActivity={compSelectedActivity}
              selectedActivityChanged={(e) =>
                setCompSelectedActivity(e.target.value)
              }
              data={compData}
              isComparison={true}
              isLoading={isLoadingComp}
              onProjectChanged={FetchComparisonProjectData}
            />
          </Grid>
        </Grid>
      </Grid>
      <Grid item xs>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={12} md={6}>
            <Grid container ref={gridRef} flexDirection={"column"}>
              <Grid item xs>
                {!data ? (
                  <Skeleton
                    variant="rectangular"
                    width={"100%"}
                    height={heightGrid1}
                  />
                ) : (
                  <>
                    {activityData && !compActivityData && (
                      <Paper elevation={3}>
                        <OverallBarChart
                          average={activityData && activityData.average}
                          height={heightGrid1}
                          selection={selectedActivity}
                          attribute={attribute}
                        />
                      </Paper>
                    )}
                    {activityData && compActivityData && (
                      <Paper elevation={3}>
                        <OverallComparisonBarChart
                          height={heightGrid1}
                          data1={activityData && activityData.average}
                          data2={compActivityData && compActivityData.average}
                          pc1={data.projectCode}
                          pc2={compData?.projectCode as string}
                          attribute={attribute}
                        />
                      </Paper>
                    )}
                  </>
                )}
              </Grid>
              <Grid item mt={2} xs>
                {!data ? (
                  <Skeleton
                    variant="rectangular"
                    width={"100%"}
                    height={heightGrid1}
                  />
                ) : (
                  <Paper elevation={3}>
                    {!compData && (
                      <PercentageHistogramChart
                        // max={data.overall.usersHistogram.numUsers} // TODO needed?
                        height={heightGrid1}
                        data1={activityData && activityData.percentageHistogram}
                        attribute={attribute}
                        selection={selectedActivity}
                      />
                    )}
                    {compData && (
                      <PercChangeHistogramChart
                        height={heightGrid1}
                        data1={
                          activityData &&
                          activityData.percentageHistogram &&
                          activityData.percentageHistogram
                        }
                        data2={
                          compActivityData &&
                          compActivityData.percentageHistogram &&
                          compActivityData.percentageHistogram
                        }
                        attribute={attribute}
                      />
                    )}
                  </Paper>
                )}
              </Grid>
            </Grid>
          </Grid>
          <Grid item xs={12} sm={12} md={6}>
            {!data ? (
              <Skeleton
                variant="rectangular"
                width={"100%"}
                height={radarGridHeight}
              />
            ) : (
              <>
                <Paper elevation={3}>
                  <LoadsRadarChart
                    height={radarGridHeight}
                    selection={selectedActivity}
                    deviceType={data.deviceType}
                    data1={
                      activityData?.deviceLoads && activityData.deviceLoads
                    }
                    data2={
                      compActivityData?.deviceLoads &&
                      compActivityData.deviceLoads
                    }
                    pc1={data.projectCode}
                    pc2={compData && compData.projectCode}
                  />
                </Paper>
              </>
            )}
          </Grid>
          <Grid item xs>
            {!data ? (
              <Skeleton
                variant="rectangular"
                width={"100%"}
                height={heightGrid1}
              />
            ) : (
              <>
                <Paper elevation={3}>
                  <ActivityComparisonBarChart
                    showPcLabel={compActivityData ? true : false}
                    selected1={selectedActivity}
                    selected2={compSelectedActivity}
                    height={heightGrid1}
                    data1={data}
                    data2={compData}
                    pc1={data.projectCode}
                    pc2={compData && compData.projectCode}
                    attribute={attribute}
                  />
                </Paper>
              </>
            )}
          </Grid>
        </Grid>
      </Grid>
      {powerpointPopup && (
        <GenerateDashboardPPTXPopup
          props={{
            selectedActivity,
            selectedProject,
            activityData,
            startDate,
            endDate,
            compActivityData,
            compSelectedActivity,
            compProject,
            compStartdate,
            compEnddate,
          }}
          handleClose={() => setPowerpointPopup(false)}
          open={powerpointPopup}
        />
      )}
    </Grid>
  );
}
